import { formatCurrency } from '@/lib/utils';
import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Download, Unlink, Link2, AlertTriangle, CheckCircle } from 'lucide-react';

const round2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;

export const downloadReceipt = async (api, receipt) => {
  try {
    const res = await api.get(`/cash-expenses/${receipt.id}/attachment`, { responseType: 'blob' });
    let name = receipt.attachment_filename || 'receipt';
    const cd = res.headers['content-disposition'];
    const m = cd && cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (m && m[1]) name = m[1].replace(/['"]/g, '');
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', name);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch {
    toast.error('Failed to download receipt');
  }
};

export const ShiftReceipts = ({ receipts, confirmedPayouts, canEdit, onChanged }) => {
  const { api } = useAuth();
  const total = round2(receipts.reduce((sum, r) => sum + r.amount, 0));
  const payouts = round2(confirmedPayouts);
  const diff = round2(total - payouts);

  const unlink = async (id) => {
    try {
      await api.post(`/cash-expenses/${id}/unmatch`);
      toast.success('Receipt unlinked from shift');
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to unlink receipt');
    }
  };

  if (!receipts.length && !payouts) return null;

  return (
    <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-3" data-testid="shift-linked-receipts">
      <div className="flex items-center gap-2">
        <Link2 className="h-4 w-4 text-green-700" />
        <span className="text-sm font-medium text-green-800">Receipts linked to this shift ({receipts.length})</span>
      </div>
      {receipts.length === 0 ? (
        <p className="text-xs text-green-700">No receipts linked yet.</p>
      ) : (
        <div className="space-y-2">
          {receipts.map(r => (
            <div key={r.id} className="flex items-center gap-3 p-2 rounded border bg-white border-green-200" data-testid={`linked-receipt-${r.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-medium">{formatCurrency(r.amount)}</span>
                  <span className="text-sm text-gray-600 truncate">{r.supplier_reason}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{r.expense_date}</span>
                  {r.branch_tag && <Badge variant="outline" className="text-xs">{r.branch_tag}</Badge>}
                </div>
              </div>
              {r.has_attachment && (
                <Button variant="ghost" size="icon" onClick={() => downloadReceipt(api, r)} title="Download" data-testid={`linked-receipt-download-${r.id}`}>
                  <Download className="h-4 w-4" />
                </Button>
              )}
              {canEdit && (
                <Button variant="ghost" size="icon" className="text-red-600" onClick={() => unlink(r.id)} title="Unlink" data-testid={`linked-receipt-unlink-${r.id}`}>
                  <Unlink className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between pt-2 border-t border-green-200 text-sm">
        <span>Linked <span className="font-mono font-medium" data-testid="linked-receipts-total">{formatCurrency(total)}</span> / payouts <span className="font-mono">{formatCurrency(payouts)}</span></span>
        {diff === 0 ? (
          <span className="flex items-center gap-1 text-green-700" data-testid="linked-receipts-status"><CheckCircle className="h-4 w-4" />Matches</span>
        ) : (
          <span className="flex items-center gap-1 text-amber-700" data-testid="linked-receipts-status"><AlertTriangle className="h-4 w-4" />Difference {formatCurrency(diff)}</span>
        )}
      </div>
    </div>
  );
};
